import { useNavigate } from "@/router";
import { useParams } from "react-router-dom";
import { SECTION_PEOPLE_CHANGE_REQUESTS_PATH, SECTION_PEOPLE_CHANGE_REQUESTS_SETTINGS_PATH } from "../../_utils";
import { useQuery } from "@apollo/client";
import { changeTypeToDisplayName, approvalTypeToDisplayName, ChangeSetting } from "./utils";
import { SECTION_CHANGEFORM_SETTINGS_QUERY } from "./queries";
import Table, { TableHeading, Row, buildActionColumn } from "@/components/Table";
import usePageTitle from "@/hooks/usePageTitle";
import { useDenyAuthorization } from "@/hooks/useCapabilities";
import { useEffect, useState } from "react";
import EditModal from "./_components/EditModal";
import Breadcrumb from "@/components/Breadcrumb";
import EmptyState from "@/components/EmptyState";
import PageHeading from "@/components/PageHeading";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { actionId } from "@/hooks/useTableRowMenu";

const columnConfig = {
  enableSorting: false
};

export default function ChangeRequestSettings() {
  const [settingId, setEditSettingId] = useState<string>();
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const listPath = SECTION_PEOPLE_CHANGE_REQUESTS_PATH;
  const { isCompactLayout } = useIsCompactLayout();
  usePageTitle(SECTION_PEOPLE_CHANGE_REQUESTS_SETTINGS_PATH, "Sections | Section Details | Change Requests | Settings");

  const { data, loading } = useQuery(SECTION_CHANGEFORM_SETTINGS_QUERY, {
    variables: { sectionId },
    skip: !sectionId
  });

  const denyAuthorization = useDenyAuthorization();

  useEffect(() => {
    if (data && !data.section.permissions.manageChangeformSettings) denyAuthorization();
  }, [data, denyAuthorization]);

  const { changeformSettings = [], name: sectionName } = data?.section || {};

  function closeEditModal() {
    setEditSettingId(undefined);
  }

  const sortedChangeformSettings = [...changeformSettings].sort((a, b) => a.changeType.localeCompare(b.changeType));

  const selectedSetting = changeformSettings.find((setting) => setting.id === settingId);

  const actionColumn = buildActionColumn<ChangeSetting>({
    onEditClick: (row) => setEditSettingId(row.id),
    editButtonTitle: "Edit setting",
    actionId,
    setMenu: () => {}
  });

  if (!sectionId) {
    return null;
  }

  return (
    <div>
      {selectedSetting && <EditModal sectionId={sectionId} setting={selectedSetting} onClosed={closeEditModal} />}
      <Breadcrumb
        title="Change Requests"
        backLink={{
          to: listPath,
          params: { sectionId }
        }}
        compactTitle="Settings"
        onBackClick={() =>
          navigate(listPath, {
            params: { sectionId }
          })
        }
      />
      <PageHeading title={sectionName ? `${sectionName} Settings` : "Settings"} compact={!isCompactLayout} />
      <TableHeading
        heading="Change Request Approval Settings"
        body="Set or update the active (manual) approvals required for member and associate change requests."
      />
      <Table
        loading={loading}
        columns={[
          {
            ...columnConfig,
            accessorKey: "changeType",
            header: "Change Type",
            cell: ({ getValue }) => changeTypeToDisplayName(getValue())
          },
          {
            ...columnConfig,
            accessorKey: "approvalType",
            header: "Approval Type",
            cell: ({ getValue }) => approvalTypeToDisplayName(getValue())
          },
          actionColumn
        ]}
        data={sortedChangeformSettings}
        onClickRow={({ original: { id } }: Row<ChangeSetting>) => setEditSettingId(id)}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Change Request Settings"
            caption="There are no approval settings configured for this section."
            iconName="settings"
          />
        )}
      />
    </div>
  );
}
